import type {
  AnswerMap,
  AnswerValue,
  Condition,
  ConditionGroup,
  FieldDef,
  SectionDef,
  TemplateDefinition,
} from './types.js';
import { isBlankAnswer } from './values.js';

export interface EvalContext {
  answers: AnswerMap;
  /** Values of the current repeater row, if evaluating inside a row. */
  row?: Record<string, AnswerValue>;
}

function isGroup(x: Condition | ConditionGroup): x is ConditionGroup {
  return 'all' in x || 'any' in x;
}

function resolve(fieldId: string, ctx: EvalContext): AnswerValue | undefined {
  if (ctx.row && fieldId in ctx.row) return ctx.row[fieldId];
  return ctx.answers[fieldId];
}

function scalar(a: AnswerValue | undefined): unknown {
  if (!a || a.kind !== 'value') return undefined;
  const v = a.value as unknown;
  if (v && typeof v === 'object' && 'selected' in (v as object)) {
    return (v as { selected: string[] }).selected;
  }
  if (v && typeof v === 'object' && 'amount' in (v as object)) {
    return (v as { amount: number }).amount;
  }
  return v;
}

function asNumber(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return undefined;
}

export function evaluateCondition(c: Condition, ctx: EvalContext): boolean {
  const answer = resolve(c.field, ctx);
  const v = scalar(answer);
  switch (c.op) {
    case 'empty':
      return isBlankAnswer(answer);
    case 'not_empty':
      return !isBlankAnswer(answer);
    case 'equals':
      if (Array.isArray(v)) return v.length === 1 && v[0] === c.value;
      return v === c.value;
    case 'not_equals':
      // A blank (or hidden) field has no value to compare, so it never satisfies not_equals.
      // This prevents dependent fields from being revealed by an unanswered question.
      if (isBlankAnswer(answer)) return false;
      if (Array.isArray(v)) return !(v.length === 1 && v[0] === c.value);
      return v !== c.value;
    case 'contains_any': {
      const wanted = Array.isArray(c.value) ? c.value : [c.value];
      if (Array.isArray(v)) return v.some((x) => wanted.includes(x as string));
      return wanted.includes(v as string);
    }
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const n = asNumber(v);
      const m = asNumber(c.value);
      if (n === undefined || m === undefined) return false;
      if (c.op === 'gt') return n > m;
      if (c.op === 'gte') return n >= m;
      if (c.op === 'lt') return n < m;
      return n <= m;
    }
    default:
      return false;
  }
}

export function evaluateGroup(g: ConditionGroup | undefined, ctx: EvalContext): boolean {
  if (!g) return true;
  const items = (x: Condition | ConditionGroup) =>
    isGroup(x) ? evaluateGroup(x, ctx) : evaluateCondition(x, ctx);
  const allOk = g.all ? g.all.every(items) : true;
  const anyOk = g.any ? (g.any.length === 0 ? true : g.any.some(items)) : true;
  return allOk && anyOk;
}

export function collectConditionFields(g: ConditionGroup | undefined, out = new Set<string>()): Set<string> {
  if (!g) return out;
  for (const x of [...(g.all ?? []), ...(g.any ?? [])]) {
    if (isGroup(x)) collectConditionFields(x, out);
    else out.add(x.field);
  }
  return out;
}

/** Index of every field (including repeater children) by id, with parent info. */
export interface FieldIndexEntry {
  field: FieldDef;
  section: SectionDef;
  parent?: FieldDef;
}

export function indexFields(t: Pick<TemplateDefinition, 'sections'>): Map<string, FieldIndexEntry> {
  const map = new Map<string, FieldIndexEntry>();
  for (const section of t.sections) {
    for (const field of section.fields) {
      map.set(field.id, { field, section });
      for (const child of field.children ?? []) {
        map.set(child.id, { field: child, section, parent: field });
      }
    }
  }
  return map;
}

/**
 * A field is visible when its section is visible, its parent repeater (if any) is
 * visible, and its own visibleWhen evaluates true. Hidden dependencies propagate:
 * a condition referencing a hidden field sees that field as blank.
 */
export function computeVisibility(
  t: Pick<TemplateDefinition, 'sections'>,
  answers: AnswerMap,
): { sections: Set<string>; fields: Set<string> } {
  const sectionsVisible = new Set<string>();
  const fieldsVisible = new Set<string>();
  // Iterate to a fixed point so chains of dependencies resolve regardless of order.
  const effective: AnswerMap = { ...answers };
  for (let pass = 0; pass < 10; pass++) {
    let changed = false;
    for (const section of t.sections) {
      const sv = evaluateGroup(section.visibleWhen, { answers: effective });
      if (sv !== sectionsVisible.has(section.id)) changed = true;
      if (sv) sectionsVisible.add(section.id);
      else sectionsVisible.delete(section.id);
      for (const f of section.fields) {
        const fv = sv && evaluateGroup(f.visibleWhen, { answers: effective });
        if (fv !== fieldsVisible.has(f.id)) changed = true;
        if (fv) {
          fieldsVisible.add(f.id);
          if (answers[f.id]) effective[f.id] = answers[f.id]!;
        } else {
          fieldsVisible.delete(f.id);
          delete effective[f.id];
        }
      }
    }
    if (!changed && pass > 0) break;
  }
  return { sections: sectionsVisible, fields: fieldsVisible };
}

/** Detects cycles in visibleWhen/requiredWhen dependency graph. Returns cycles as field-id paths. */
export function detectConditionCycles(t: Pick<TemplateDefinition, 'sections'>): string[][] {
  const idx = indexFields(t);
  const graph = new Map<string, string[]>();
  for (const section of t.sections) {
    const sectionDeps = [...collectConditionFields(section.visibleWhen)];
    for (const f of section.fields) {
      const deps = new Set([...collectConditionFields(f.visibleWhen), ...collectConditionFields(f.requiredWhen), ...sectionDeps]);
      graph.set(f.id, [...deps]);
      for (const c of f.children ?? []) {
        const cdeps = new Set([
          ...collectConditionFields(c.visibleWhen),
          ...collectConditionFields(c.requiredWhen),
          ...deps,
        ]);
        graph.set(c.id, [...cdeps]);
      }
    }
  }
  const cycles: string[][] = [];
  const state = new Map<string, 0 | 1 | 2>();
  const stack: string[] = [];
  const visit = (id: string) => {
    state.set(id, 1);
    stack.push(id);
    for (const dep of graph.get(id) ?? []) {
      if (!idx.has(dep)) continue;
      const s = state.get(dep) ?? 0;
      if (s === 1) {
        const start = stack.indexOf(dep);
        cycles.push([...stack.slice(start), dep]);
      } else if (s === 0) visit(dep);
    }
    stack.pop();
    state.set(id, 2);
  };
  for (const id of graph.keys()) if ((state.get(id) ?? 0) === 0) visit(id);
  return cycles;
}
